import { apiClient } from './client';
import type {
  School,
  SchoolsResponse,
  SchoolsStats,
  SchoolFilters,
  SchoolEditData,
  SchoolOptions,
  CreateSchoolData,
  CreateSchoolResponse,
  UpdateSchoolData,
  UpdateSchoolResponse,
  UploadFileResponse,
  RneLookupResponse,
  Academie,
  SchoolMapItem,
  DashboardData,
} from '@/types/school';

export async function getSchools(filters: SchoolFilters = {}): Promise<SchoolsResponse> {
  const params = new URLSearchParams();

  if (filters.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }
  if (filters.vip !== undefined && filters.vip !== '') {
    params.append('vip', filters.vip);
  }
  if (filters.version) {
    params.append('version', filters.version);
  }
  if (filters.network) {
    params.append('network', filters.network);
  }
  if (filters.academie) {
    params.append('academie', filters.academie);
  }
  if (filters.favorites) {
    params.append('favorites', filters.favorites);
  }
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.page) {
    params.append('page', filters.page.toString());
  }
  if (filters.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters.sortBy) {
    params.append('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.append('sortOrder', filters.sortOrder);
  }

  const response = await apiClient.get<SchoolsResponse>(`/api/schools?${params.toString()}`);
  return response.data;
}

export async function getSchoolsStats(): Promise<SchoolsStats> {
  const response = await apiClient.get<SchoolsStats>('/api/schools/stats');
  return response.data;
}

export async function getDashboardData(): Promise<DashboardData> {
  const response = await apiClient.get<DashboardData>('/api/schools/dashboard');
  return response.data;
}

export async function getSchool(code: string): Promise<School> {
  const response = await apiClient.get<School>(`/api/schools/${code}`);
  return response.data;
}

export async function getSchoolForEdit(code: string): Promise<SchoolEditData> {
  const response = await apiClient.get<SchoolEditData>(`/api/schools/${code}/edit`);
  return response.data;
}

export async function getSchoolOptions(): Promise<SchoolOptions> {
  const response = await apiClient.get<SchoolOptions>('/api/schools/options');
  return response.data;
}

export async function createSchool(data: CreateSchoolData): Promise<CreateSchoolResponse> {
  const response = await apiClient.post<CreateSchoolResponse>('/api/schools', data);
  return response.data;
}

export async function updateSchool(code: string, data: UpdateSchoolData): Promise<UpdateSchoolResponse> {
  const response = await apiClient.put<UpdateSchoolResponse>(`/api/schools/${code}`, data);
  return response.data;
}

export async function deleteSchool(code: string): Promise<void> {
  await apiClient.delete(`/api/schools/${code}`);
}

export async function checkSchoolCodeExists(code: string): Promise<boolean> {
  const response = await apiClient.get<{ exists: boolean }>(`/api/schools/check-code/${code}`);
  return response.data.exists;
}

export async function uploadSchoolFile(
  code: string,
  field: 'logo' | 'logoSignin' | 'favicon' | 'cover',
  file: File
): Promise<UploadFileResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<UploadFileResponse>(
    `/api/schools/${code}/upload/${field}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

export async function updateSchoolModules(code: string, moduleIds: number[]): Promise<void> {
  await apiClient.put(`/api/schools/${code}/modules`, { moduleIds });
}

// School Metrics
export interface SchoolMetrics {
  studentCount: number;
  parentCount: number;
  classCount: number;
  teacherCount: number;
  connexionCount: number;
  mailCount: number;
  storageUsed: number;
}

export interface MetricsDataPoint {
  date: string;
  dateLabel: string;
  studentCount: number | null;
  parentCount: number | null;
  classCount: number | null;
  teacherCount: number | null;
  connexionCount: number | null;
  mailCount: number | null;
}

export interface MetricsHistory {
  schoolYear: string;
  data: MetricsDataPoint[];
}

export async function getSchoolMetrics(code: string): Promise<SchoolMetrics> {
  const response = await apiClient.get<SchoolMetrics>(`/api/schools/${code}/metrics`);
  return response.data;
}

export async function getSchoolMetricsHistory(code: string): Promise<MetricsHistory[]> {
  const response = await apiClient.get<MetricsHistory[]>(`/api/schools/${code}/metrics/history`);
  return response.data;
}

// Backups
export interface Backup {
  id: number;
  type: string;
  createdAt: string;
  fileName: string;
  size: number;
  sizeFormatted: string;
}

export interface BackupCopy {
  id: number;
  createdAt: string;
  dbName: string;
  dbUser: string;
}

export async function getSchoolBackups(code: string): Promise<Backup[]> {
  const response = await apiClient.get<Backup[]>(`/api/schools/${code}/backups`);
  return response.data;
}

export async function getSchoolCopies(code: string): Promise<BackupCopy[]> {
  const response = await apiClient.get<BackupCopy[]>(`/api/schools/${code}/copies`);
  return response.data;
}

export async function createSchoolBackup(code: string): Promise<{ id: number; fileName: string; message: string }> {
  const response = await apiClient.post<{ id: number; fileName: string; message: string }>(`/api/schools/${code}/backups`);
  return response.data;
}

export async function createSchoolCopy(code: string): Promise<{ id: number; dbName: string; message: string }> {
  const response = await apiClient.post<{ id: number; dbName: string; message: string }>(`/api/schools/${code}/copies`);
  return response.data;
}

export async function deleteSchoolBackup(code: string, backupId: number): Promise<void> {
  await apiClient.delete(`/api/schools/${code}/backups/${backupId}`);
}

export async function deleteSchoolCopy(code: string, copyId: number): Promise<void> {
  await apiClient.delete(`/api/schools/${code}/copies/${copyId}`);
}

export function getBackupDownloadUrl(code: string, backupId: number): string {
  return `/api/schools/${code}/backups/${backupId}/download`;
}

export async function copyBackupToCopy(code: string, backupId: number): Promise<{ id: number; dbName: string; message: string }> {
  const response = await apiClient.post<{ id: number; dbName: string; message: string }>(`/api/schools/${code}/backups/${backupId}/copy`);
  return response.data;
}

// Favorites
export interface ToggleFavoriteResponse {
  isFavorite: boolean;
  favoriteSchoolIds: number[];
}

export async function toggleFavoriteSchool(code: string): Promise<ToggleFavoriteResponse> {
  const response = await apiClient.post<ToggleFavoriteResponse>(`/api/schools/${code}/favorite`);
  return response.data;
}

// V2 Subscription
export interface UpdateV2SubscriptionData {
  offerId?: number | null;
  offerBillingStartDate?: string | null;
  offerBillingEndDate?: string | null;
  options?: Array<{
    id?: number;
    productOptionId: number;
    billingStartDate: string;
    billingEndDate?: string | null;
  }>;
}

import type { V2Subscription } from '@/types/school';

export interface UpdateV2SubscriptionResponse {
  message: string;
  subscription: V2Subscription;
}

export async function updateV2Subscription(
  code: string,
  data: UpdateV2SubscriptionData
): Promise<UpdateV2SubscriptionResponse> {
  const response = await apiClient.put<UpdateV2SubscriptionResponse>(
    `/api/schools/${code}/v2-subscription`,
    data
  );
  return response.data;
}

// RNE Lookup (direct)
export async function lookupRne(rne: string): Promise<RneLookupResponse> {
  const response = await apiClient.get<RneLookupResponse>(`/api/schools/lookup-rne/${encodeURIComponent(rne)}`);
  return response.data;
}

// Search establishments by RNE or name
export interface EtablissementSearchResult {
  rne: string | null;
  codeAcademie: string | null;
  name: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  type: string | null;
  sector: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface EtablissementSearchResponse {
  type: 'rne' | 'name';
  results: EtablissementSearchResult[];
}

export async function searchEtablissement(query: string): Promise<EtablissementSearchResponse> {
  const response = await apiClient.get<EtablissementSearchResponse>(`/api/schools/search-etablissement`, {
    params: { q: query }
  });
  return response.data;
}

// Academies
export async function getAcademies(): Promise<Academie[]> {
  const response = await apiClient.get<Academie[]>('/api/schools/academies');
  return response.data;
}

// Address geocoding
export interface AddressSuggestion {
  label: string;
  latitude: number;
  longitude: number;
  city: string | null;
  postcode: string | null;
  context: string | null;
}

export async function geocodeAddress(query: string, country: string = 'FR'): Promise<AddressSuggestion[]> {
  const response = await apiClient.get<AddressSuggestion[]>('/api/schools/geocode-address', {
    params: { q: query, country }
  });
  return response.data;
}

// Map
export async function getSchoolsForMap(params?: { academie?: string; status?: string; version?: string; network?: string }): Promise<SchoolMapItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.academie) searchParams.append('academie', params.academie);
  if (params?.status) searchParams.append('status', params.status);
  if (params?.version) searchParams.append('version', params.version);
  if (params?.network) searchParams.append('network', params.network);
  const query = searchParams.toString();
  const response = await apiClient.get<SchoolMapItem[]>(`/api/schools/map${query ? `?${query}` : ''}`);
  return response.data;
}
